import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const stats = [
  { value: '2+', label: 'Years Experience' },
  { value: '4+', label: 'Apps Delivered' },
  { value: 'MERN', label: 'Core Stack' },
  { value: 'B.IT', label: 'Degree (2024–)' },
];

const lines = [
  { indent: 0, text: 'const aryan = {', color: '#f0f6ff' },
  { indent: 1, key: 'role', value: '"Full Stack Developer"', vc: '#86efac' },
  { indent: 1, key: 'stack', value: '["React", "Node", "MongoDB"]', vc: '#fdba74' },
  { indent: 1, key: 'location', value: '"Sydney, Australia"', vc: '#86efac' },
  { indent: 1, key: 'education', value: '"B.IT @ Kent Institute"', vc: '#86efac' },
  { indent: 1, key: 'seeking', value: '"Graduate / Junior roles"', vc: '#a5b4fc' },
  { indent: 1, key: 'available', value: 'true', vc: '#34d399' },
  { indent: 0, text: '}', color: '#f0f6ff' },
];

export default function About() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="about" style={{ padding: '120px 0' }}>
      <div className="section-container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '5rem', alignItems: 'center' }}>

          <div ref={ref}>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="section-label">
              About Me
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.1 }} className="section-title" style={{ marginBottom: '1.5rem' }}>
              Motivated developer,{' '}
              <span style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                building real products
              </span>
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} style={{ color: '#64748b', lineHeight: 1.85, fontSize: '0.93rem', marginBottom: '1.25rem' }}>
              I'm a Full Stack Developer completing my Bachelor of Information Technology at Kent Institute Australia.
              Experienced in building real-world MERN apps — from e-commerce platforms to school management systems —
              with a strong eye for UI/UX design and clean, scalable code.
            </motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.25 }} style={{ color: '#64748b', lineHeight: 1.85, fontSize: '0.93rem', marginBottom: '2rem' }}>
              Actively seeking graduate or junior developer opportunities where I can contribute, grow, and keep shipping great software.
            </motion.p>
            <motion.a initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.35 }} href="#contact" className="glass-btn glass-btn-primary" style={{ display: 'inline-flex' }}>
              Let's Connect →
            </motion.a>
          </div>

          <motion.div initial={{ opacity: 0, x: 40 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>
            <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '18px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 1.2rem', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                {['#ff5f56','#ffbd2e','#27c93f'].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c, opacity: 0.8 }} />)}
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', color: '#475569', marginLeft: '0.4rem' }}>aryan.js</span>
              </div>
              <div style={{ padding: '1.5rem 1.75rem', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', lineHeight: 2.1 }}>
                {lines.map((line, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.4 + i * 0.07 }} style={{ paddingLeft: line.indent ? '1.5rem' : 0 }}>
                    {line.key ? (
                      <>
                        <span style={{ color: '#a5b4fc' }}>{line.key}</span>
                        <span style={{ color: '#64748b' }}>: </span>
                        <span style={{ color: line.vc }}>{line.value}</span>
                        <span style={{ color: '#64748b' }}>,</span>
                      </>
                    ) : (
                      <span style={{ color: line.color }}>{line.text}</span>
                    )}
                  </motion.div>
                ))}
                <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: [0, 1, 0] } : {}} transition={{ delay: 1.2, duration: 0.9, repeat: Infinity }} style={{ display: 'inline-block', width: 7, height: 15, background: '#6366f1', verticalAlign: 'middle' }} />
              </div>
            </div>
          </motion.div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginTop: '5rem' }}>
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.5 + i * 0.08 }}
              className="glass-card" style={{ padding: '1.5rem', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.9rem', fontWeight: 700, background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1, marginBottom: '0.4rem' }}>{s.value}</p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: '#475569', letterSpacing: '0.04em' }}>{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
